import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Home, Utensils, Car, Coffee, PiggyBank, CheckCircle2, PieChart, ShieldCheck, ArrowRight } from 'lucide-react';
// --- Types & Data ---
type Envelope = {
  id: string;
  category: string;
  icon: React.ElementType;
  allocated: number;
  spent: number;
};
const envelopes: Envelope[] = [{
  id: '1',
  category: 'Housing',
  icon: Home,
  allocated: 2000,
  spent: 2000
}, {
  id: '2',
  category: 'Food',
  icon: Utensils,
  allocated: 800,
  spent: 450
}, {
  id: '3',
  category: 'Transport',
  icon: Car,
  allocated: 400,
  spent: 320
}, {
  id: '4',
  category: 'Entertainment',
  icon: Coffee,
  allocated: 300,
  spent: 120
}, {
  id: '5',
  category: 'Savings',
  icon: PiggyBank,
  allocated: 500,
  spent: 0
}];
const features = [{
  title: 'Easy Category Management',
  description: 'Create and customize envelopes for every part of your life.',
  icon: Wallet
}, {
  title: 'Visual Spending Tracking',
  description: 'See exactly where your money goes with intuitive progress bars.',
  icon: PieChart
}, {
  title: 'Stay Within Budget',
  description: 'Stop overspending by knowing exactly what is left in each envelope.',
  icon: ShieldCheck
}, {
  title: 'Simple & Intuitive',
  description: 'No complex spreadsheets. Just digital envelopes that make sense.',
  icon: CheckCircle2
}];
// --- Components ---
const EnvelopeCard = ({
  item,
  index
}: {
  item: Envelope;
  index: number;
}) => {
  const percentage = Math.min(item.spent / item.allocated * 100, 100);
  const remaining = item.allocated - item.spent;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} viewport={{
    once: true,
    margin: '-50px'
  }} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 border border-[#E8E4D9] relative overflow-hidden group" data-id="element-0">
      {/* Decorative top border acting as the "envelope flap" hint */}
      <div className="absolute top-0 left-0 w-full h-1 bg-[#A8B5A0] opacity-50" data-id="element-1" />

      <div className="flex items-start justify-between mb-4" data-id="element-2">
        <div className="flex items-center gap-3" data-id="element-3">
          <div className="p-2.5 bg-[#F5F1E8] rounded-full text-[#D4735E]" data-id="element-4">
            <item.icon size={20} strokeWidth={2} data-id="element-5" />
          </div>
          <h3 className="font-semibold text-lg text-[#2D2D2D]" data-id="element-6">
            {item.category}
          </h3>
        </div>
        <span className="text-sm font-medium text-[#A8B5A0] bg-[#F5F1E8] px-2 py-1 rounded-md" data-id="element-7">
          {item.allocated} PLN
        </span>
      </div>

      <div className="space-y-2" data-id="element-8">
        <div className="flex justify-between text-sm" data-id="element-9">
          <span className="text-[#D4735E] font-medium" data-id="element-10">
            Spent: {item.spent} PLN
          </span>
          <span className="text-[#6B7280]" data-id="element-11">Left: {remaining} PLN</span>
        </div>

        {/* Progress Bar Container */}
        <div className="h-3 w-full bg-[#A8B5A0]/20 rounded-full overflow-hidden relative" data-id="element-12">
          {/* Background indicating "Remaining" (Sage) is implicit by the container color,
            but let's make the fill (Spent) Terracotta. */}
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: `${percentage}%`
        }} transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.2 + index * 0.1
        }} className="h-full bg-[#D4735E] rounded-full" data-id="element-13" />
        </div>
      </div>
    </motion.div>;
};
const FeatureCard = ({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.5,
  delay: index * 0.1
}} viewport={{
  once: true
}} className="flex flex-col items-center text-center p-6" data-id="element-14">
    <div className="mb-4 p-4 bg-white rounded-2xl shadow-sm text-[#D4735E] border border-[#E8E4D9]" data-id="element-15">
      <feature.icon size={28} strokeWidth={1.5} data-id="element-16" />
    </div>
    <h3 className="text-lg font-semibold text-[#2D2D2D] mb-2" data-id="element-17">
      {feature.title}
    </h3>
    <p className="text-[#6B7280] leading-relaxed max-w-xs" data-id="element-18">
      {feature.description}
    </p>
  </motion.div>;
// --- Main App ---
export function App() {
  return <div className="min-h-screen bg-[#F5F1E8] text-[#2D2D2D] font-sans selection:bg-[#D4735E] selection:text-white" data-id="element-19">
      {/* Navigation / Header */}
      <nav className="w-full px-6 py-6 max-w-7xl mx-auto flex justify-between items-center" data-id="element-20">
        <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-[#2D2D2D]" data-id="element-21">
          <Wallet className="text-[#D4735E]" data-id="element-22" />
          <span data-id="element-23">Envelope Budget</span>
        </div>
        <button className="text-sm font-medium text-[#2D2D2D] hover:text-[#D4735E] transition-colors" data-id="element-24">
          Log In
        </button>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto" data-id="element-25">
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-20" data-id="element-26">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="flex-1 space-y-8 text-center md:text-left" data-id="element-27">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-[#2D2D2D]" data-id="element-28">
              Budgeting that feels <span className="text-[#D4735E]" data-id="element-29">real</span>.
            </h1>
            <p className="text-lg md:text-xl text-[#6B7280] max-w-lg mx-auto md:mx-0 leading-relaxed" data-id="element-30">
              Take control of your spending with the time-tested envelope
              method. Allocate your income, track every zloty, and save with
              confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-31">
              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="px-8 py-3.5 bg-[#D4735E] text-white rounded-full font-medium shadow-lg shadow-[#D4735E]/20 hover:bg-[#C0624D] transition-colors flex items-center justify-center gap-2" data-id="element-32">
                Start Budgeting <ArrowRight size={18} data-id="element-33" />
              </motion.button>
              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="px-8 py-3.5 bg-white text-[#2D2D2D] border border-[#E8E4D9] rounded-full font-medium hover:bg-[#FAF9F6] transition-colors" data-id="element-34">
                Learn More
              </motion.button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex-1 w-full max-w-md" data-id="element-35">
            {/* Abstract Hero Visual */}
            <div className="relative aspect-square md:aspect-[4/3] bg-white rounded-3xl shadow-xl border border-[#E8E4D9] p-8 flex flex-col justify-center items-center overflow-hidden" data-id="element-36">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#F5F1E8] rounded-full -mr-32 -mt-32 opacity-50" data-id="element-37" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#A8B5A0] rounded-full -ml-24 -mb-24 opacity-20" data-id="element-38" />

              <div className="relative z-10 text-center space-y-6 w-full max-w-xs" data-id="element-39">
                <div className="w-20 h-20 bg-[#F5F1E8] rounded-2xl mx-auto flex items-center justify-center text-[#D4735E] shadow-sm" data-id="element-40">
                  <Wallet size={40} data-id="element-41" />
                </div>
                <div className="space-y-2" data-id="element-42">
                  <div className="h-4 bg-[#F5F1E8] rounded-full w-3/4 mx-auto" data-id="element-43" />
                  <div className="h-4 bg-[#F5F1E8] rounded-full w-1/2 mx-auto" data-id="element-44" />
                </div>
                <div className="pt-4 flex justify-center gap-3" data-id="element-45">
                  <div className="w-12 h-12 rounded-full bg-[#A8B5A0]/20 flex items-center justify-center text-[#A8B5A0]" data-id="element-46">
                    <Home size={20} data-id="element-47" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#D4735E]/20 flex items-center justify-center text-[#D4735E]" data-id="element-48">
                    <Utensils size={20} data-id="element-49" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#F5F1E8] flex items-center justify-center text-[#6B7280]" data-id="element-50">
                    <Car size={20} data-id="element-51" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Envelopes Section */}
      <section className="px-6 py-20 bg-white/50 border-y border-[#E8E4D9]/50" data-id="element-52">
        <div className="max-w-5xl mx-auto" data-id="element-53">
          <div className="text-center mb-16" data-id="element-54">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl font-bold text-[#2D2D2D] mb-4" data-id="element-55">
              Your Budget Envelopes
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] max-w-xl mx-auto" data-id="element-56">
              Visualize your funds in distinct categories. When an envelope is
              empty, you know exactly where you stand.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-57">
            {envelopes.map((envelope, index) => <EnvelopeCard key={envelope.id} item={envelope} index={index} data-id="element-58" />)}

            {/* Add New Envelope Card (Visual only) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: envelopes.length * 0.1
          }} viewport={{
            once: true
          }} className="bg-[#F5F1E8]/50 rounded-2xl p-6 border-2 border-dashed border-[#A8B5A0]/30 flex flex-col items-center justify-center min-h-[180px] cursor-pointer hover:bg-[#F5F1E8] hover:border-[#A8B5A0] transition-all group" data-id="element-59">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#A8B5A0] group-hover:text-[#D4735E] group-hover:scale-110 transition-all shadow-sm mb-3" data-id="element-60">
                <span className="text-2xl font-light" data-id="element-61">+</span>
              </div>
              <span className="font-medium text-[#6B7280] group-hover:text-[#2D2D2D]" data-id="element-62">
                Add Envelope
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-24 max-w-7xl mx-auto" data-id="element-63">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-64">
          {features.map((feature, index) => <FeatureCard key={index} feature={feature} index={index} data-id="element-65" />)}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-[#D4735E] text-white" data-id="element-66">
        <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-67">
          <h2 className="text-3xl md:text-4xl font-bold" data-id="element-68">
            Ready to master your money?
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto" data-id="element-69">
            Join thousands of people who have found financial peace of mind
            using the envelope method.
          </p>
          <button className="px-8 py-4 bg-white text-[#D4735E] rounded-full font-bold text-lg hover:bg-[#F5F1E8] transition-colors shadow-xl" data-id="element-70">
            Create Your First Envelope
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-[#2D2D2D] text-white/60 text-sm" data-id="element-71">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-72">
          <div className="flex items-center gap-2 text-white" data-id="element-73">
            <Wallet size={20} data-id="element-74" />
            <span className="font-bold text-lg" data-id="element-75">Envelope Budget</span>
          </div>

          <div className="text-center md:text-right" data-id="element-76">
            <p data-id="element-77">ul. Przykładowa 123</p>
            <p data-id="element-78">00-001 Warszawa, Poland</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-white/10 text-center md:text-left flex flex-col md:flex-row justify-between" data-id="element-79">
          <p data-id="element-80">
            &copy; {new Date().getFullYear()} Envelope Budget. All rights
            reserved.
          </p>
          <div className="flex gap-6 justify-center md:justify-end mt-4 md:mt-0" data-id="element-81">
            <a href="#" className="hover:text-white transition-colors" data-id="element-82">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-83">
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-84">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>;
}